/**
 * CuddleCuratePage — Customer-facing photo curation board for Cuddle Prints.
 *
 * The admin seeds a story in the Cuddle Studio (title, child's name, a few
 * starter photos), then hits "Copy customer link" and sends the URL to the
 * customer. The customer opens the URL and can add/remove/reorder photos and
 * add captions. Everything saves automatically.
 *
 * Auth: the URL carries a per-story `share_token`. Same backend endpoints
 * as CuratePage — the backend accepts either submission_token or share_token.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import UploadBanner from "../components/UploadBanner";
import {
  Loader2, Upload, Trash2, ImagePlus, GripVertical, Check,
  AlertCircle, Heart,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_BG = "#0B0D12";
const PANEL_BG = "#0F1115";
const BORDER = "#1E232D";
const ACCENT = "#E6C982";

const btnGhost =
  `inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md ` +
  `border border-[${BORDER}] text-white/80 hover:border-white/30 hover:text-white ` +
  `text-xs transition`;

export default function CuddleCuratePage() {
  const { sid, token } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const isUploading = uploadQueue.some(
    (q) => q.status === "queued" || q.status === "uploading"
  );

  const fetchSubmission = async () => {
    try {
      const r = await axios.get(`${API}/studio/public/submissions/${sid}?token=${token}`);
      setSubmission(r.data.submission);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open this story");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmission(); }, [sid, token]);

  useEffect(() => {
    document.title = submission
      ? `${submission.pet_name || "Your child"}'s Book · The Cuddle Prints`
      : "The Cuddle Prints";
  }, [submission?.pet_name]);

  useEffect(() => {
    if (uploadQueue.length > 0 && uploadQueue.every((q) => q.status === "done")) {
      const t = setTimeout(() => setUploadQueue([]), 2000);
      return () => clearTimeout(t);
    }
  }, [uploadQueue]);

  const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const _uploadOneWithRetry = async (itemId, file, maxRetries = 3) => {
    const url = `${API}/studio/public/submissions/${sid}/photos?token=${token}`;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(url, fd, { timeout: 60000 });
        setUploadQueue((q) =>
          q.map((x) => (x.id === itemId ? { ...x, status: "done" } : x))
        );
        return;
      } catch (e) {
        if (attempt < maxRetries) {
          await _sleep(1000 * Math.pow(2, attempt));
          continue;
        }
        const msg = e?.response?.data?.detail || "Upload failed";
        setUploadQueue((q) =>
          q.map((x) => (x.id === itemId ? { ...x, status: "failed", error: msg } : x))
        );
        throw e;
      }
    }
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    const snapshot = Array.from(files);
    const newItems = snapshot.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      status: "queued",
      file: f,
    }));
    setUploadQueue((q) => [...q.filter((x) => x.status !== "done"), ...newItems]);
    for (const item of newItems) {
      setUploadQueue((q) =>
        q.map((x) => (x.id === item.id ? { ...x, status: "uploading" } : x))
      );
      try {
        await _uploadOneWithRetry(item.id, item.file);
      } catch { /* status already set to "failed" */ }
    }
    await fetchSubmission();
  };

  const retryFailed = async () => {
    const failed = uploadQueue.filter((q) => q.status === "failed");
    setUploadQueue((q) =>
      q.map((x) => x.status === "failed" ? { ...x, status: "queued", error: undefined } : x)
    );
    for (const item of failed) {
      setUploadQueue((q) =>
        q.map((x) => (x.id === item.id ? { ...x, status: "uploading" } : x))
      );
      try { await _uploadOneWithRetry(item.id, item.file); } catch { /* already marked failed */ }
    }
    await fetchSubmission();
  };

  const dismissBanner = () => setUploadQueue([]);

  const deletePhoto = async (photoId) => {
    if (!confirm("Remove this photo?")) return;
    try {
      await axios.delete(`${API}/studio/public/submissions/${sid}/photos/${photoId}?token=${token}`);
      await fetchSubmission();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveCaption = async (photoId, caption, { silent = false } = {}) => {
    try {
      await axios.patch(
        `${API}/studio/public/submissions/${sid}/photos/${photoId}?token=${token}`,
        { caption }
      );
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const flushCaptionBeacon = (photoId, caption) => {
    try {
      const url = `${API}/studio/public/submissions/${sid}/photos/${photoId}?token=${token}`;
      fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption }),
        keepalive: true,
      });
    } catch { /* best-effort */ }
  };

  const reorderPhotos = async (nextIds) => {
    try {
      await axios.post(
        `${API}/studio/public/submissions/${sid}/photos/reorder?token=${token}`,
        { photo_ids: nextIds }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E6C982]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-3 text-red-400" />
          <p className="text-white/70 text-sm">{error}</p>
          <p className="text-white/40 text-xs mt-3">Ask the sender for a fresh link if this one has expired.</p>
        </div>
      </div>
    );
  }

  const photos = submission.photos || [];
  const childName = submission.pet_name || "your child";

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white">
      {uploadQueue.length > 0 && (
        <UploadBanner queue={uploadQueue} onRetry={retryFailed} onDismiss={dismissBanner} sticky />
      )}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6C982]/80 mb-2">
          The Cuddle Prints
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif leading-tight mb-3">
          {childName}&rsquo;s photo book
        </h1>
        <p className="text-white/60 text-sm leading-relaxed mb-8">
          Below are the photos we have so far. You can add more, remove any you
          don&rsquo;t want, drag them into the order you&rsquo;d like, and add a short
          caption to each one — a memory, a moment, a feeling. Everything saves
          automatically; just close the tab when you&rsquo;re done.
        </p>

        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-white/50" data-testid="cuddle-curate-photo-count">
            {photos.length} photo{photos.length === 1 ? "" : "s"}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={isUploading}
            className={btnGhost}
            data-testid="cuddle-curate-add-photo-btn"
          >
            {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {isUploading ? "Uploading…" : "Add photos"}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic"
            className="hidden"
            onChange={(e) => {
              uploadFiles(e.target.files);
              if (fileRef.current) fileRef.current.value = "";
            }}
            data-testid="cuddle-curate-file-input"
          />
        </div>

        {photos.length === 0 ? (
          <EmptyDropzone onClick={() => fileRef.current?.click()} uploading={isUploading} />
        ) : (
          <PhotoGrid
            photos={photos}
            onDelete={deletePhoto}
            onCaption={saveCaption}
            onFlushBeacon={flushCaptionBeacon}
            onReorder={(next) => {
              setSubmission({ ...submission, photos: next });
              reorderPhotos(next.map((p) => p.id));
            }}
          />
        )}

        <div className="mt-10 border border-[#1E232D] rounded-lg p-4 bg-[#0F1115]" data-testid="cuddle-curate-help">
          <div className="flex items-center gap-2 text-[#E6C982] text-xs uppercase tracking-wider mb-1.5">
            <Heart className="w-3.5 h-3.5" /> A tip
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Captions don&rsquo;t need to be perfect — something like &ldquo;{childName}&rsquo;s
            first birthday cake&rdquo; or &ldquo;her favourite spot in the garden&rdquo;
            gives us plenty to build a beautiful, personal page around.
          </p>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Empty dropzone --------------------------- */
function EmptyDropzone({ onClick, uploading }) {
  return (
    <button
      onClick={onClick}
      disabled={uploading}
      className="w-full min-h-[260px] rounded-xl border-2 border-dashed border-[#1E232D] hover:border-[#E6C982]/60 bg-[#0F1115] flex flex-col items-center justify-center gap-3 text-white/50 hover:text-white transition"
      data-testid="cuddle-curate-empty-dropzone"
    >
      {uploading ? (
        <Loader2 className="w-7 h-7 animate-spin text-[#E6C982]" />
      ) : (
        <>
          <ImagePlus className="w-10 h-10 text-[#E6C982]" />
          <div className="text-sm">Tap to add photos</div>
          <div className="text-[11px] text-white/30">JPG · PNG · WEBP · HEIC · up to 12 MB each</div>
        </>
      )}
    </button>
  );
}

/* --------------------------- Sortable grid --------------------------- */
function PhotoGrid({ photos, onDelete, onCaption, onFlushBeacon, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = useMemo(() => photos.map((p) => p.id), [photos]);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(e) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIx = ids.indexOf(active.id);
        const newIx = ids.indexOf(over.id);
        onReorder(arrayMove(photos, oldIx, newIx));
      }}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="cuddle-curate-grid">
          {photos.map((p, idx) => (
            <PhotoCard
              key={p.id}
              photo={p}
              index={idx}
              onDelete={() => onDelete(p.id)}
              onCaption={(c, opts) => onCaption(p.id, c, opts)}
              onFlushBeacon={(c) => onFlushBeacon(p.id, c)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

/* --------------------------- Individual card --------------------------- */
function PhotoCard({ photo, index, onDelete, onCaption, onFlushBeacon }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: photo.id });
  const [caption, setCaption] = useState(photo.caption || "");
  const [status, setStatus] = useState("idle");

  const debounceRef = useRef(null);
  const savedTickRef = useRef(null);
  const retryRef = useRef(null);
  const retryAttemptsRef = useRef(0);
  const captionRef = useRef(caption);
  const lastSavedRef = useRef(photo.caption || "");
  useEffect(() => { captionRef.current = caption; }, [caption]);

  const commit = async ({ isRetry = false } = {}) => {
    const value = captionRef.current || "";
    if (value === (lastSavedRef.current || "")) return;
    setStatus("saving");
    try {
      await onCaption(value, { silent: isRetry });
      lastSavedRef.current = value;
      retryAttemptsRef.current = 0;
      setStatus("saved");
      clearTimeout(savedTickRef.current);
      savedTickRef.current = setTimeout(() => setStatus("idle"), 1400);
    } catch {
      setStatus("error");
      const attempt = retryAttemptsRef.current + 1;
      retryAttemptsRef.current = attempt;
      if (attempt <= 4) {
        const delay = Math.min(2000 * 2 ** (attempt - 1), 16000);
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(() => {
          if ((captionRef.current || "") !== (lastSavedRef.current || "")) commit({ isRetry: true });
        }, delay);
      } else {
        setStatus("failed");
      }
    }
  };

  useEffect(() => {
    const isFocused = document.activeElement?.dataset?.testid === `cuddle-curate-caption-${photo.id}`;
    const hasPending = status === "pending" || status === "saving" || status === "error" || status === "failed";
    if (isFocused || hasPending) return;
    setCaption(photo.caption || "");
    lastSavedRef.current = photo.caption || "";
  }, [photo.caption, photo.id]);

  useEffect(() => {
    const flushOnHide = () => {
      const value = captionRef.current || "";
      if (value === (lastSavedRef.current || "")) return;
      clearTimeout(debounceRef.current);
      clearTimeout(retryRef.current);
      onFlushBeacon(value);
      lastSavedRef.current = value;
      setStatus("saved");
    };
    const onVis = () => { if (document.visibilityState === "hidden") flushOnHide(); };
    window.addEventListener("beforeunload", flushOnHide);
    window.addEventListener("pagehide", flushOnHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flushOnHide);
      window.removeEventListener("pagehide", flushOnHide);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const onCaptionChange = (next) => {
    setCaption(next);
    setStatus("pending");
    retryAttemptsRef.current = 0;
    clearTimeout(debounceRef.current);
    clearTimeout(retryRef.current);
    debounceRef.current = setTimeout(commit, 800);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-[#0F1115] border border-[#1E232D] rounded-lg overflow-hidden"
      data-testid={`cuddle-curate-card-${photo.id}`}
    >
      <div className="relative aspect-square">
        <img src={photo.url} alt="" className="w-full h-full object-cover" />
        <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
          {index + 1}
        </div>
        <button
          {...attributes}
          {...listeners}
          className="absolute top-1.5 right-1.5 p-1 bg-black/60 rounded hover:bg-black/90 touch-none"
          aria-label="Drag to reorder"
          data-testid={`cuddle-curate-drag-${photo.id}`}
        >
          <GripVertical className="w-3.5 h-3.5 text-white" />
        </button>
        <button
          onClick={onDelete}
          className="absolute bottom-1.5 right-1.5 p-1 bg-black/60 rounded hover:bg-red-500/90 text-white"
          aria-label="Delete photo"
          data-testid={`cuddle-curate-delete-${photo.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2">
        <textarea
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          onBlur={() => { clearTimeout(debounceRef.current); commit(); }}
          placeholder="Add a caption or memory…"
          rows={2}
          className="w-full bg-transparent text-white text-xs placeholder:text-white/30 focus:outline-none resize-none"
          data-testid={`cuddle-curate-caption-${photo.id}`}
        />
        <div className="flex items-center justify-end h-4 text-[10px]" data-testid={`cuddle-curate-caption-status-${photo.id}`}>
          {status === "pending" && <span className="text-white/40">unsaved…</span>}
          {status === "saving" && (
            <span className="text-white/50 flex items-center gap-0.5">
              <Loader2 className="w-3 h-3 animate-spin" /> saving
            </span>
          )}
          {status === "saved" && (
            <span className="text-emerald-400 flex items-center gap-0.5">
              <Check className="w-3 h-3" /> saved
            </span>
          )}
          {status === "error" && (
            <span className="text-amber-400 flex items-center gap-0.5">
              <AlertCircle className="w-3 h-3" /> retrying…
            </span>
          )}
          {status === "failed" && (
            <button
              onClick={() => { retryAttemptsRef.current = 0; commit(); }}
              className="text-red-400 flex items-center gap-0.5 hover:text-red-300 underline"
              data-testid={`cuddle-curate-caption-retry-${photo.id}`}
            >
              <AlertCircle className="w-3 h-3" /> save failed · tap to retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
